import { IClassifiedPolicies, IInsurancePolicyLineOfBusiness } from '../../../../extViewmodels/sdk/models';

const CATEGORY_ORDER = ['Personal', 'Commercial', 'Other'];
const FALLBACK_CATEGORY = 'All Lines of Business';

/**
 * Groups a flat list of LOBs into ordered categories (Personal, Commercial, Other)
 * using the classified policies API as the source of truth for name→category mapping.
 * Falls back to a flat "All Lines of Business" group if no classified data is available.
 */
export function groupLobsByCategory(
	flatLobs: IInsurancePolicyLineOfBusiness[],
	classifiedPolicies?: IClassifiedPolicies
): [string, IInsurancePolicyLineOfBusiness[]][] {
	if (classifiedPolicies?.linesOfBusiness) {
		const nameToCategory: Record<string, string> = {};
		for (const [category, lobs] of Object.entries(classifiedPolicies.linesOfBusiness)) {
			for (const lob of lobs) {
				nameToCategory[lob.label] = category;
			}
		}

		const grouped: Record<string, IInsurancePolicyLineOfBusiness[]> = {};
		for (const lob of flatLobs) {
			const category = nameToCategory[lob.name] || 'Other';
			if (!grouped[category]) {
				grouped[category] = [];
			}
			grouped[category].push(lob);
		}

		// Return in defined order, then any extra categories alphabetically
		const ordered: [string, IInsurancePolicyLineOfBusiness[]][] = [];
		for (const cat of CATEGORY_ORDER) {
			if (grouped[cat]?.length > 0) {
				ordered.push([cat, grouped[cat]]);
			}
		}
		for (const [cat, lobs] of Object.entries(grouped)) {
			if (!CATEGORY_ORDER.includes(cat) && lobs.length > 0) {
				ordered.push([cat, lobs]);
			}
		}
		return ordered;
	}

	if (flatLobs.length > 0) {
		return [[FALLBACK_CATEGORY, flatLobs]];
	}
	return [];
}

/**
 * True when groupLobsByCategory produced real categories (classified data was available),
 * false when it fell back to the flat single-group shape. Callers should render a flat list
 * in the fallback case instead of showing the fallback group as a pseudo-category.
 */
export function hasCategoryGrouping(lobCategories: [string, IInsurancePolicyLineOfBusiness[]][]): boolean {
	return lobCategories.length > 1 || (lobCategories.length === 1 && lobCategories[0][0] !== FALLBACK_CATEGORY);
}

export function toggleCategorySelection<T>(
	lobsInCategory: T[],
	selected: string[],
	getKey: (lob: T) => string
): string[] {
	const keys = lobsInCategory.map(getKey);
	const allSelected = keys.every(key => selected.includes(key));
	if (allSelected) {
		return selected.filter(key => !keys.includes(key));
	}
	const toAdd = keys.filter(key => !selected.includes(key));
	return [...selected, ...toAdd];
}

export function isCategoryFullySelected<T>(
	lobsInCategory: T[],
	selected: string[],
	getKey: (lob: T) => string
): boolean {
	return lobsInCategory.length > 0 && lobsInCategory.every(lob => selected.includes(getKey(lob)));
}

export function isCategoryPartiallySelected<T>(
	lobsInCategory: T[],
	selected: string[],
	getKey: (lob: T) => string
): boolean {
	return (
		lobsInCategory.some(lob => selected.includes(getKey(lob))) &&
		!isCategoryFullySelected(lobsInCategory, selected, getKey)
	);
}
